import { useEffect, useRef, useState } from "react";
import { Sparkles, Users, Star, Repeat } from "lucide-react";

const stats = [
  { icon: Sparkles, value: 5000, suffix: "+", label: "Properties Cleaned", description: "Across Melbourne" },
  { icon: Users, value: 2500, suffix: "+", label: "Happy Clients", description: "And counting" },
  { icon: Star, value: 4.9, suffix: "★", label: "Average Rating", description: "Based on Google Reviews", decimals: 1 },
  { icon: Repeat, value: 98, suffix: "%", label: "Repeat Customers", description: "Trust us again" },
];

function useCountUp(target: number, duration: number, start: boolean, decimals = 0) {
  const [current, setCurrent] = useState(0);
  const rafRef = useRef<number>();

  useEffect(() => {
    if (!start) return;
    const startTime = performance.now();
    const animate = (now: number) => {
      const elapsed = now - startTime;
      const progress = Math.min(elapsed / duration, 1);
      const eased = 1 - Math.pow(1 - progress, 3);
      setCurrent(parseFloat((eased * target).toFixed(decimals)));
      if (progress < 1) rafRef.current = requestAnimationFrame(animate);
    };
    rafRef.current = requestAnimationFrame(animate);
    return () => { if (rafRef.current) cancelAnimationFrame(rafRef.current); };
  }, [start, target, duration, decimals]);

  return current;
}

const StatBlock = ({ icon: Icon, value, suffix, label, description, decimals = 0, animate }: typeof stats[0] & { animate: boolean }) => {
  const count = useCountUp(value, 1800, animate, decimals);
  return (
    <div className="flex flex-col items-center text-center gap-3">
      <div className="w-12 h-12 rounded-full bg-[#FFD400] flex items-center justify-center">
        <Icon className="w-5 h-5 text-[#111111]" strokeWidth={2} />
      </div>
      <div>
        <span className="text-4xl md:text-5xl font-black text-[#111111] leading-none tracking-tight">
          {decimals ? count.toFixed(decimals) : Math.round(count).toLocaleString()}{suffix}
        </span>
      </div>
      <div>
        <p className="font-bold text-[#111111] text-sm">{label}</p>
        <p className="text-[#111111]/70 text-xs mt-0.5">{description}</p>
      </div>
    </div>
  );
};

const SocialProofStats = () => {
  const sectionRef = useRef<HTMLDivElement>(null);
  const [animate, setAnimate] = useState(false);

  useEffect(() => {
    const el = sectionRef.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) { setAnimate(true); observer.disconnect(); } },
      { threshold: 0.3 }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  return (
    <section ref={sectionRef} className="bg-white py-20 md:py-28">
      <div className="container max-w-5xl text-center">
        <h2 className="text-3xl md:text-4xl font-black text-[#111111] leading-tight">
          Trusted Across Melbourne
        </h2>
        <p className="text-[#111111]/70 mt-2 text-base mb-14">
          The numbers behind our commitment to a better clean.
        </p>

        <div className="grid grid-cols-2 sm:grid-cols-4 gap-12 md:gap-10 max-w-4xl mx-auto">
          {stats.map((s) => (
            <StatBlock key={s.label} {...s} animate={animate} decimals={s.decimals ?? 0} />
          ))}
        </div>
      </div>
    </section>
  );
};

export default SocialProofStats;
